// CartContext.js
import React, { createContext, useState, useContext, useCallback } from 'react';
import { AuthContext } from './AuthContext';
import { groupByZone, regroupAndRenumber, sameZone } from '../components/cartZones';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const { user } = useContext(AuthContext);

  // Le modifiche partono SEMPRE dall'ultima versione del carrello (aggiornamento funzionale):
  // prima si partiva dal carrello di inizio clic, e con due modifiche nello stesso clic
  // (es. Duplica riga = aggiungi + sconto, Rinomina zona su più righe) la seconda cancellava la prima.
  // saveCart/loadCart stabili (useCallback): la barra laterale chiama loadCart() all'avvio con
  // [loadCart] tra le dipendenze; se cambiava a ogni render ricaricava il carrello in continuo
  // e una ricarica poteva sovrascrivere uno spostamento appena fatto (es. "Sposta su" che tornava giù).
  const saveCart = useCallback((items) => {
    setCart((prevCart) => {
      const storingItems = typeof items === "function" ? items(prevCart) : items;
      try {
        localStorage.setItem("cart", JSON.stringify(storingItems));
      } catch (err) {
        console.error("Error saving cart to local storage");
      }
      return storingItems;
    });
  }, []);

  const loadCart = useCallback((items = null) => {
    console.log("loadCart:", items);
    try {
      if (items == null) {
        let storedItems = localStorage.getItem("cart");
        let storingItems = JSON.parse(storedItems);
        if (storingItems == null) setCart([]);
        else {
          setCart(storingItems);
        }
      } else {
        saveCart(items);
      }
    } catch (err) {
      setCart([]);
      localStorage.removeItem("cart");
      console.error("Error saving cart to local storage");
    }
  }, [saveCart]);

  const addToCart = (item) => {
  const discount = user?.discount || 0;

  saveCart(prevCart => {
    const nextPosition = prevCart.length + 1;

    const newItem = {
      ...item,
      id: item.id || Date.now(),
      discount,
      position: item.position ?? nextPosition,
    };

    return [...prevCart, newItem];
  });
};

  const updateCartItem = (id, field, value) => {
    saveCart(prevCart => {
      const next = prevCart.map(item => {
        if (item.id === id) {
          console.log(`Updating item with id: ${id}, field: ${field}, value: ${value}`);
          return { ...item, [field]: value };
        }
        return item;
      });
      // Cambio zona: la riga si sposta nel suo gruppo, numerazione aggiornata.
      return field === 'zone' ? regroupAndRenumber(next) : next;
    });
  };

  // Sostituisce il contenuto di una riga (es. "Modifica" dal configuratore) tenendo
  // id, posizione, zona e sconto di riga. Se la riga non c'è più viene aggiunta in fondo.
  const replaceCartItem = (id, fields) => {
    saveCart(prevCart => {
      if (!prevCart.some(item => item.id === id)) {
        return [...prevCart, { ...fields, id, discount: user?.discount || 0, position: prevCart.length + 1 }];
      }
      return prevCart.map(item => (item.id === id ? { ...item, ...fields, id: item.id } : item));
    });
  };

  const updateCartItemNotes = (id, notes) => {
    saveCart(prevCart =>
      prevCart.map(item => {
        if (item.id === id) {
          console.log(`Updating notes for item with id: ${id}, notes: ${notes}`);
          return { ...item, notes };
        }
        return item;
      })
    );
  };

  const moveCartItem = (id, direction) => {
  saveCart(prevCart => {
    // Ordine come visualizzato (raggruppato per zona): si sposta solo dentro la propria zona.
    const items = groupByZone(prevCart);
    const index = items.findIndex(i => i.id === id);
    const targetIndex = index + direction;

    if (targetIndex < 0 || targetIndex >= items.length || !sameZone(items[index], items[targetIndex])) {
      return prevCart;
    }

    // swap
    [items[index], items[targetIndex]] = [items[targetIndex], items[index]];

    // rinumera
    return items.map((item, idx) => ({
      ...item,
      position: idx + 1,
    }));
  });
};


  // Sposta un'intera zona (con tutte le sue righe) prima o dopo la zona vicina.
  const moveZone = (zone, direction) => {
    saveCart(prevCart => {
      const key = (z) => String(z ?? '').trim().toLowerCase();
      const groups = [];
      groupByZone(prevCart).forEach((item) => {
        const last = groups[groups.length - 1];
        if (last && last.key === key(item.zone)) last.items.push(item);
        else groups.push({ key: key(item.zone), items: [item] });
      });
      const index = groups.findIndex((g) => g.key === key(zone));
      const target = index + direction;
      if (index < 0 || target < 0 || target >= groups.length) return prevCart;
      [groups[index], groups[target]] = [groups[target], groups[index]];
      return groups.flatMap((g) => g.items).map((item, idx) => ({ ...item, position: idx + 1 }));
    });
  };

  const removeFromCart = (id) => {
    saveCart(prevCart => prevCart.filter(item => item.id !== id));
  };

  const clearCart = () => {
    // Svuota stato e storage locale per evitare che il carrello riappaia dopo logout/login
    setCart([]);
    try {
      localStorage.removeItem("cart");
    } catch (err) {
      console.error("Error removing cart from local storage", err);
    }
    setCurrentOrder(null); // Aggiunto per resettare l'ordine corrente quando si svuota il carrello
  };

  const loadOrderIntoCart = (order) => {
    console.log("loadOrderIntoCart:", order);
    //clearCart();
    //console.log("currentCart:", cart);
    setCurrentOrder(order); // Imposta l'ordine corrente
    loadCart(order.items.map((e, index) => {
  return {
    ...e,
    id: e._id ?? Date.now() + Math.random(),
    position: e.position ?? index + 1,
  };
}));
    /*order.items.forEach(item => {
      addToCart({
        ...item,
        id: item.id || Date.now() + Math.random(), // Garantisce un ID univoco per ogni articolo
      });
    });*/
  };

  // Duplica un ordine esistente come NUOVO ordine.
  // Non imposta currentOrder (resta null) così il carrello eseguirà un POST:
  // sarà il backend ad assegnare un nuovo numero di preventivo.
  // Riferimento/note/destinatario/pagamento vengono precompilati tramite la
  // "draft meta" letta dal Cart al mount (deve combaciare con DRAFT_META_KEY in Cart.js).
  const DRAFT_META_KEY = 'cartDraftMeta_v1';

  const duplicateOrderIntoCart = (order) => {
    if (!order) return;
    console.log("duplicateOrderIntoCart:", order);

    setCurrentOrder(null);

    // Copia gli articoli rigenerando gli id locali e scartando gli _id del DB
    const items = (order.items || []).map((e, index) => {
      const copy = {
        ...e,
        id: Date.now() + Math.random(),
        position: e.position ?? index + 1,
      };
      delete copy._id; // non riusare l'_id Mongo dell'articolo originale
      return copy;
    });
    loadCart(items);

    // Precompila i dati commerciali del nuovo ordine
    try {
      const r = order.recipient || {};
      const p = order.payment || {};
      const meta = {
        reference: order.reference ? `${order.reference} (copia)` : '(copia)',
        notes: order.notes || '',
        recipient: {
          recipientType: r.recipientType || 'lead',
          name: r.name || '',
          company: r.company || '',
          email: r.email || '',
          phone: r.phone || '',
          address: r.address || '',
          vat: r.vat || '',
        },
        payment: {
          method: p.method || 'bank_transfer',
          terms: p.terms || '',
          dueDays: p.dueDays ?? '',
          depositPercent: p.depositPercent ?? '',
          notes: p.notes || '',
        },
      };
      localStorage.setItem(DRAFT_META_KEY, JSON.stringify(meta));
    } catch (err) {
      console.error("Errore durante la preparazione della copia dell'ordine", err);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        setCart,
        addToCart,
        updateCartItem,
        updateCartItemNotes,
        replaceCartItem,
        removeFromCart,
        clearCart,
        loadOrderIntoCart,
        duplicateOrderIntoCart,
        moveCartItem,
        moveZone,
        currentOrder,
        setCurrentOrder, // Aggiunto per poter impostare l'ordine corrente da altri componenti
        saveCart,
        loadCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
